import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, InputAdornment,
    LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    Switch, TablePagination, TableRow, TableSortLabel, TextField, ToggleButton,
    ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getSchedule, updateSalesTimeline, setInstallationConfirmed, setSiteVisitArranging, getSalesById, getSalesDocuments } from '../../api/salesApi';
import { openWhatsApp } from '../../utils/phoneUtils';
import {
    buildMilestoneMsg, canMessage, hasMessage, jobFromRow, MILESTONE_MESSAGES, PRE_INSTALL_PCT,
} from './scheduleMessage';
import { useUser } from '../../context/userContext';
import SchedulingCalendar from './schedulingCalendar';
import { paymentLabel } from '../../utils/ccPricing';

const PP = "'Poppins', sans-serif";

/* ============================================================================
   ALL SCHEDULING — who is booked to which customer, and whether the money kept up

   (user 2026-10-04: "we can have a schedule and payment ... so the scheduling can be
   in a page, who is schedule to which customer? then when they click, then can do. so
   like site visit etc, they can update in here too under scheduling")

   It sits under Sales in the menu, beside All Sales, and it shows the caller their own
   sales only — the API decides that, not this page ("so each of their sales can see
   their own scheduling only").

   The whole list arrives in one call and the filtering happens here. An ops person
   switches between "this week" and "unscheduled" constantly; a round trip per switch
   makes a working page feel broken.
   ========================================================================= */

const DAY = 24 * 60 * 60 * 1000;

// The four dates this page schedules, in the order they happen. SEDA submission and
// approval are on the sale page's timeline too, but they are paperwork rather than an
// appointment, so the board keeps them in the drawer and out of the table.
const BOOKABLE = [
    { key: 'siteVisitOn', label: 'Site Visit', short: 'Site Visit' },
    { key: 'deliveryOn', label: 'Product Delivery', short: 'Delivery' },
    { key: 'installationOn', label: 'Installation', short: 'Installation' },
    { key: 'tnbApprovedOn', label: 'TNB Approval', short: 'TNB' },
];

// Everything the timeline write takes. The API replaces all six on every save, so a
// partial payload would silently clear the dates it left out.
const ALL_DATE_KEYS = [
    'sedaSubmittedOn', 'sedaApprovedOn', 'siteVisitOn',
    // The loan date rides along on every save even for a cash sale, where it is null
    // anyway. Leaving it out would be worse than useless: the write replaces all the
    // dates, so saving an installation date from this board would silently wipe a loan
    // date keyed on the sale page. (user 2026-10-11)
    'loanApplicationOn',
    'deliveryOn', 'installationOn', 'tnbApprovedOn',
];

// A loan sale is any sale whose payment method mentions a loan — matched on the string
// because historical rows were written by three screens in three casings.
const isLoanSale = (row) => /loan/i.test(row?.paymentMethod || '');

// The loan date shows only on a loan sale: a permanently empty box on every cash job
// would train people to ignore the drawer.
const DRAWER_FIELDS = [
    { key: 'sedaSubmittedOn', label: 'SEDA Docs Submitted' },
    { key: 'sedaApprovedOn', label: 'SEDA Approval' },
    ...BOOKABLE,
];
const LOAN_FIELDS = [
    { key: 'loanApplicationOn', label: 'Loan Application' },
];
// Loan FIRST, matching the sale page's timeline and the table's column order: the bank
// has to pay before anything downstream moves, and a milestone that appears in three
// places in two different orders is read wrong in at least one of them.
const drawerFieldsFor = (row) => (isLoanSale(row)
    ? [...LOAN_FIELDS, ...DRAWER_FIELDS]
    : DRAWER_FIELDS);

const headSx = {
    fontFamily: PP, fontWeight: 700, fontSize: '0.74rem', color: '#475569',
    bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap',
};
const cellSx = { fontFamily: PP, fontSize: '0.82rem', color: '#1E293B' };

// The actions ride along the right-hand edge. With nine columns the table is wider than a
// laptop screen, and what scrolled out of reach was the WhatsApp button — the one thing
// the row is for. Sticky keeps it on screen whatever the width.
const stickySx = {
    position: 'sticky', right: 0, zIndex: 2, bgcolor: '#fff',
    borderLeft: '1px solid #F1F5F9', whiteSpace: 'nowrap',
};
const stickyHeadSx = { ...stickySx, bgcolor: '#F8FAFC', zIndex: 3 };

// RM with cents only when there are any. toLocaleString() on its own drops a trailing
// zero, so RM 16,826.60 printed as "RM 16,826.6" — wrong above a list of slips that add
// up to it, and wrong anywhere money is read.
const money = (v) => {
    const n = Number(v) || 0;
    return 'RM ' + n.toLocaleString('en-MY', {
        minimumFractionDigits: n % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    });
};

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

const asDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};
// "22 Jun 26". The year matters on this board: half the jobs on it were installed last
// year, and "22 Jun" alone reads as next week. (user 2026-10-05: "its 22jun26 better")
const fmtShort = (v) => {
    const d = asDate(v);
    return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
};
const fmtLong = (v) => {
    const d = asDate(v);
    return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};
// ISO -> 'YYYY-MM-DD' for <input type=date>, in LOCAL parts so the picker shows the
// stored day rather than a timezone-shifted one.
const toDateInput = (v) => {
    const d = asDate(v);
    if (!d) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};
// 'YYYY-MM-DD' -> ISO at noon, which cannot roll over a day in either direction.
const toIso = (s) => (s ? new Date(`${s}T12:00:00`).toISOString() : null);

/**
 * How long before the installation the money has to be in. The SAME figure the backend
 * watcher rings its bell on (Services/SchedulePaymentScheduler.cs) — if one of them
 * changes, change both, or the board and the bell will say different things about the
 * same job. (user 2026-10-05: "once got installation date, 2weeks b4 installation date
 * need to get payment")
 */
const PAY_LEAD_DAYS = 14;

/** The day the money has to be in by: a fortnight before the installation. */
const paymentDueOn = (row) => {
    const d = asDate(row.installationOn);
    return d ? new Date(d.getTime() - PAY_LEAD_DAYS * DAY) : null;
};

/**
 * What to say about a job beyond its dates.
 *
 * The company's terms, and the ones the customer is sent in the installation message, so
 * the board flags against the sentence the customer actually read
 * (user 2026-10-05: "Payment 60% of total payment is required before installation and
 * remaining balance after installation process", and "once got installation date, 2weeks
 * b4 installation date need to get payment"):
 *
 *   before installation   PRE_INSTALL_PCT of the total, due a fortnight before the date
 *   after installation    the remaining balance
 *
 *   Past             the installation day has gone — the job happened.
 *   Pending payment  money that is already overdue: the 60% past its deadline, or the
 *                    balance after the crew has been.
 *   60% due …        an installation is booked, the 60% is not in, and its deadline is
 *                    still ahead — a date to work to rather than a failure.
 *
 * A sale with no installation date gets nothing: nothing has been promised to anybody
 * yet, so there is nothing to be late for.
 */
const remarksFor = (row, today) => {
    const out = [];
    const install = asDate(row.installationOn);
    if (!install) return out;

    const total = Number(row.effectiveTotal) || 0;
    const pct = total > 0 ? (Number(row.paidTotal) / total) * 100 : 0;
    const due = paymentDueOn(row);
    const installed = install < today;

    if (installed) out.push({ key: 'past', label: 'Past', bg: '#F1F5F9', fg: '#475569' });
    if (total <= 0) return out;

    const overdue = { key: 'pendingPay', label: 'Pending payment', bg: '#FEE2E2', fg: '#991B1B' };

    if (installed) {
        // The crew has been: everything is due now.
        if (pct < 100) out.push(overdue);
    } else if (pct < PRE_INSTALL_PCT) {
        if (due <= today) out.push(overdue);
        else {
            out.push({
                key: 'payDue', bg: '#FEF3C7', fg: '#92400E',
                label: `${PRE_INSTALL_PCT}% due ${fmtShort(due)}`,
            });
        }
    }
    return out;
};

const FILTERS = [
    { key: 'week', label: 'This week' },
    { key: 'fortnight', label: 'Next 14 days' },
    { key: 'past', label: 'Past' },
    { key: 'all', label: 'All' },
];

export default function SchedulingPage() {
    const navigate = useNavigate();
    const { user } = useUser();
    const [rows, setRows] = useState([]);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('fortnight');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    // Soonest-first is the view an ops person wants on opening; any column can take over.
    // (user 2026-10-05: "able to sort")
    const [sortBy, setSortBy] = useState('installationOn');
    const [sortDir, setSortDir] = useState('asc');
    // Table or month grid. Remembered per browser, like the Events list's own toggle —
    // whichever one somebody works in, they work in it every day.
    // (user 2026-10-05: "can we have mode of view one is table view, one is calendar view?")
    const [view, setView] = useState(() => {
        try { return localStorage.getItem('schedulingView') === 'calendar' ? 'calendar' : 'table'; }
        catch { return 'table'; }
    });
    const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

    const chooseView = (v) => {
        if (!v) return;                                   // the group fires null on re-click
        setView(v);
        try { localStorage.setItem('schedulingView', v); } catch { /* private window */ }
    };
    const [open, setOpen] = useState(null);        // the row in the drawer
    const [draft, setDraft] = useState({});        // its dates, as the inputs hold them
    const [busy, setBusy] = useState(false);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const [arrangeBusy, setArrangeBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveErr, setSaveErr] = useState('');

    const today = useMemo(startOfToday, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getSchedule();
            setRows(res.data?.rows || []);
            setCanEdit(!!res.data?.canEdit);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // "Next 14 days" is the right view for a company with jobs booked — and an empty
    // screen for one that has not started booking them, which reads as a broken page.
    // So the default drops to Everything once, on the first load, when there is nothing
    // ahead. After that the person's own choice stands.
    const [defaultPicked, setDefaultPicked] = useState(false);
    useEffect(() => {
        if (loading || defaultPicked || rows.length === 0) return;
        setDefaultPicked(true);
        const ahead = rows.some(r => {
            const d = asDate(r.installationOn);
            return d && d >= today && (d - today) / DAY <= PAY_LEAD_DAYS;
        });
        if (!ahead) {
            // Nothing is coming, so the board opens on everything, newest work at the top,
            // rather than on an empty "next 14 days".
            setFilter('all');
            setSortDir('desc');
        }

        // The month grid opens on TODAY, always — a scheduling calendar is about the week
        // in front of you, and a month chosen for you because it happens to hold the last
        // job of the year is a month nobody asked to see. The arrows and the Today button
        // do the rest. (user 2026-10-05: "calendar always show today")
        //
        // It briefly landed on "wherever the work is" instead. That was read from the first
        // report, made while the local API was down and the grid sat on an empty month —
        // the emptiness was no data, not the wrong month.
    }, [loading, rows, defaultPicked, today]);

    // Decorate once: every row carries its flag and its next date, so filtering,
    // sorting and rendering all read the same answer.
    const decorated = useMemo(() => rows.map(r => ({
        ...r,
        _remarks: remarksFor(r, today),
        _pct: Number(r.effectiveTotal) > 0
            ? Math.round((Number(r.paidTotal) / Number(r.effectiveTotal)) * 100)
            : 0,
    })), [rows, today]);

    const hasRemark = (r, key) => r._remarks.some(x => x.key === key);

    const counts = useMemo(() => {
        const installDays = (r) => {
            const d = asDate(r.installationOn);
            return d ? Math.round((d - today) / DAY) : null;
        };
        return {
            week: decorated.filter(r => { const d = installDays(r); return d !== null && d >= 0 && d <= 7; }).length,
            payment: decorated.filter(r => hasRemark(r, 'pendingPay') || hasRemark(r, 'payDue')).length,
            noDate: decorated.filter(r => !r.installationOn).length,
        };
    }, [decorated, today]);

    // What the search box leaves, before any date filtering — the calendar wants this.
    const searched = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return decorated;
        return decorated.filter(r =>
            (r.customerName || '').toLowerCase().includes(q)
            || (r.agentName || '').toLowerCase().includes(q)
            // The team is on screen now, so it has to be searchable — typing "Freedom"
            // should bring back that team's jobs.
            || (r.groupName || '').toLowerCase().includes(q)
            || (r.coAgentName || '').toLowerCase().includes(q)
            || (r.city || '').toLowerCase().includes(q)
            || (r.state || '').toLowerCase().includes(q)
            || String(r.salesId) === q);
    }, [decorated, search]);

    const shownRows = useMemo(() => {
        let list = searched;

        // Every filter reads the INSTALLATION date. A sale with no installation date is in
        // All and nowhere else — it has not been scheduled yet, so it is neither this week
        // nor past.
        if (filter !== 'all') {
            list = list.filter(r => {
                const d = asDate(r.installationOn);
                if (!d) return false;
                const days = Math.round((d - today) / DAY);
                if (filter === 'past') return days < 0;
                if (filter === 'week') return days >= 0 && days <= 7;
                return days >= 0 && days <= PAY_LEAD_DAYS;   // fortnight
            });
        }

        // Sorted by whichever column was asked for. A row with nothing in that column has
        // no value to sort on, so it goes LAST in both directions rather than to an
        // invented date — flipping the arrow should not fill the top of the screen with
        // blanks.
        const value = (r) => {
            switch (sortBy) {
                case 'customer': return (r.customerName || '').toLowerCase();
                case 'agent': return (r.agentName || '').toLowerCase();
                case 'payment': return r._pct;
                case 'remarks': return r._remarks.length ? r._remarks[0].label : null;
                default: { const d = asDate(r[sortBy]); return d ? d.getTime() : null; }
            }
        };
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const av = value(a);
            const bv = value(b);
            const aEmpty = av === null || av === '';
            const bEmpty = bv === null || bv === '';
            if (aEmpty && bEmpty) return b.salesId - a.salesId;
            if (aEmpty) return 1;
            if (bEmpty) return -1;
            if (av < bv) return -dir;
            if (av > bv) return dir;
            return b.salesId - a.salesId;
        });
    }, [searched, filter, sortBy, sortDir, today]);

    // Clicking the column you are already on flips the arrow; a new column starts the way
    // that column is usually read — names A-Z, dates soonest first, money lowest first.
    const sortOn = (key) => {
        if (sortBy === key) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); return; }
        setSortBy(key);
        setSortDir('asc');
    };

    useEffect(() => { setPage(0); }, [filter, search]);

    const paged = shownRows.slice(page * perPage, page * perPage + perPage);

    // ── the drawer ───────────────────────────────────────────────────────────
    // What the sale is, in the words a customer uses: size, panels, battery, phase and
    // the inverter. The board row cannot carry the inverter — nothing stores it as a
    // column; it lives inside the sale's own form snapshot — so the drawer asks the sale
    // when it opens. One call, only when somebody looks.
    // (user 2026-10-05: "kwp + panels+battery how many? ... the panels number, 650W or
    // 640W or any W ... 3phase or single phase, inverter size")
    const [spec, setSpec] = useState(null);
    // The payments behind the "Paid X of Y" line — same list the sale page's timeline
    // shows, because somebody booking a crew asks the same question of it: what has come
    // in, and when. (user 2026-10-11: "all scheduling sidebar got show payments?")
    const [payDocs, setPayDocs] = useState(null);

    // DocDate is the date the customer PAID; UploadedOn is when the slip reached us. The
    // first is what this is about, so it wins — and a slip with none says "uploaded"
    // rather than passing the upload date off as a payment date.
    const loadPayments = async (row) => {
        setPayDocs(null);
        try {
            const res = await getSalesDocuments(row.salesId);
            setPayDocs((res?.data || [])
                .filter(d => d.category === 'Deposit' || d.category === 'Payment')
                .map(d => ({
                    id: d.salesDocumentId,
                    label: d.paymentLabel || d.title || d.category,
                    amount: Number(d.amount) || 0,
                    on: d.docDate || d.uploadedOn,
                    dated: !!d.docDate,
                }))
                .sort((a, b) => new Date(a.on) - new Date(b.on)));
        } catch {
            setPayDocs([]);
        }
    };

    const loadSpec = async (row) => {
        setSpec(null);
        try {
            const res = await getSalesById(row.salesId);
            const s = res.data || {};
            let snap = {};
            try { snap = s.formPayloadJson ? JSON.parse(s.formPayloadJson) : {}; } catch { snap = {}; }
            const kwp = Number(s.energy) || Number(row.energy) || 0;
            const panels = Number(s.panelQty) || Number(row.panelQty) || 0;
            setSpec({
                kwp,
                panels,
                // The snapshot's own wattage when the sale was quoted through the calculator;
                // otherwise the honest derivation, kWp over panels.
                panelWatt: Number(snap.panelWatt) || (panels > 0 && kwp > 0 ? Math.round((kwp * 1000) / panels) : 0),
                batteryQty: Number(row.batteryQty) || Number(snap.batteryQty) || 0,
                batteryKwh: Number(row.batteryKwh) || Number(snap.batteryKwh) || 0,
                phase: row.phase || null,
                // kWac is the inverter. Only sales quoted through RSC/CSC carry one; the rest
                // show a dash rather than a number nobody can stand behind.
                inverterKw: Number(snap.kwac) || 0,
                scheme: s.scheme || null,
                paymentMethod: s.paymentMethod || null,
            });
        } catch {
            setSpec({ kwp: Number(row.energy) || 0, panels: Number(row.panelQty) || 0,
                panelWatt: Number(row.panelWatt) || 0, batteryQty: Number(row.batteryQty) || 0,
                batteryKwh: Number(row.batteryKwh) || 0, phase: row.phase || null, inverterKw: 0 });
        }
    };

    const openRow = (row) => {
        setOpen(row);
        setSaveErr('');
        setSaved(false);
        loadSpec(row);
        loadPayments(row);
        setDraft(ALL_DATE_KEYS.reduce((acc, k) => ({ ...acc, [k]: toDateInput(row[k]) }), {}));
    };

    const saveDrawer = async () => {
        if (!open) return;
        setBusy(true);
        setSaveErr('');
        try {
            await updateSalesTimeline({
                salesId: open.salesId,
                ...ALL_DATE_KEYS.reduce((acc, k) => ({ ...acc, [k]: toIso(draft[k]) }), {}),
            });
            // Patch the row in place instead of refetching the whole board: the person is
            // usually working down a filtered list and a reload would move it under them.
            const patch = ALL_DATE_KEYS.reduce((acc, k) => ({ ...acc, [k]: toIso(draft[k]) }), {});
            setRows(prev => prev.map(r => (r.salesId === open.salesId ? { ...r, ...patch } : r)));
            // The drawer stays open on the saved dates, so the usual next move — tell the
            // customer — is one button away and is sending what was actually recorded.
            setOpen(o => (o ? { ...o, ...patch } : o));
            setSaved(true);
        } catch (e) {
            setSaveErr(e?.response?.status === 403
                ? 'Your account cannot change these dates — ask the office to update them.'
                : 'Could not save. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // The customer's half of the installation date. Its own action, not part of Save:
    // the dates and this answer different questions, and mixing them would let somebody
    // tick "confirmed" in the same breath as changing the date it refers to.
    // The engineer's half of the site visit: a slot is being worked out, there is no date
    // to key yet, and a blank box would read as "nobody has started".
    // (user 2026-10-06: "site visit we dont have date ... engineer is arranging their
    // schedule will pm customer")
    const toggleArranging = async (next) => {
        if (!open) return;
        setArrangeBusy(true);
        setSaveErr('');
        try {
            const res = await setSiteVisitArranging(open.salesId, next);
            const at = res?.data?.siteVisitArrangingOn ?? null;
            setRows(prev => prev.map(r => (r.salesId === open.salesId ? { ...r, siteVisitArrangingOn: at } : r)));
            setOpen(o => (o ? { ...o, siteVisitArrangingOn: at } : o));
        } catch (e) {
            setSaveErr(e?.response?.status === 403
                ? 'Your account cannot change this — ask the office.'
                : e?.response?.data?.message || 'Could not save that. Please try again.');
        } finally {
            setArrangeBusy(false);
        }
    };

    const toggleConfirmed = async (next) => {
        if (!open) return;
        setConfirmBusy(true);
        setSaveErr('');
        try {
            const res = await setInstallationConfirmed(open.salesId, next);
            const at = res?.data?.installationConfirmedOn ?? null;
            setRows(prev => prev.map(r => (r.salesId === open.salesId ? { ...r, installationConfirmedOn: at } : r)));
            setOpen(o => (o ? { ...o, installationConfirmedOn: at } : o));
        } catch (e) {
            setSaveErr(e?.response?.status === 403
                ? 'Your account cannot confirm dates — ask the office.'
                : e?.response?.data?.message || 'Could not save that. Please try again.');
        } finally {
            setConfirmBusy(false);
        }
    };

    // Always the SAVED dates, never what is sitting unsaved in the drawer's boxes: a
    // message must not promise a date nobody recorded. Same rule as the sale page's card.
    // Each milestone has its own preset message; the row's button sends the one for
    // whatever is next. (user 2026-10-05: "each of milestone we will preset a message")
    const share = (row, key) => openWhatsApp(row.customerPhone,
        buildMilestoneMsg(key, jobFromRow(row), user?.firstName || user?.username));

    const shareReason = (row, key) => {
        if (!row?.customerPhone) return 'No phone number on this customer';
        if (!row?.[MILESTONE_MESSAGES[key]?.needs]) return `Save the ${MILESTONE_MESSAGES[key].label.toLowerCase()} date first`;
        return '';
    };

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            <Stack direction="row" alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                        All Scheduling
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP }}>
                        {counts.week} installing in the next 7 days · {counts.payment} waiting on payment · {counts.noDate} with no installation date
                    </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <TextField
                    size="small" placeholder="Customer, agent, town or sale #"
                    value={search} onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 260, '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' } }}
                />
                <ToggleButtonGroup
                    size="small" exclusive value={view} onChange={(_, v) => chooseView(v)}
                    sx={{
                        '& .MuiToggleButton-root': {
                            fontFamily: PP, textTransform: 'none', fontSize: '0.76rem', fontWeight: 600,
                            px: 1.25, py: 0.5, borderColor: '#E2E8F0', color: '#475569',
                        },
                        '& .Mui-selected': { bgcolor: '#E8F2FF !important', color: '#1A8CFF !important' },
                    }}
                >
                    <ToggleButton value="table">
                        <TableRowsIcon sx={{ fontSize: 16, mr: 0.5 }} /> Table
                    </ToggleButton>
                    <ToggleButton value="calendar">
                        <CalendarMonthIcon sx={{ fontSize: 16, mr: 0.5 }} /> Calendar
                    </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="Reload the board">
                    <IconButton onClick={load} size="small" sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                        <RefreshIcon sx={{ fontSize: 18, color: '#64748B' }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {view === 'table' && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 700, color: '#475569', mr: 0.5 }}>
                    Installation
                </Typography>
                {FILTERS.map(f => (
                    <Chip
                        key={f.key} label={f.label} size="small"
                        onClick={() => setFilter(f.key)}
                        sx={{
                            fontFamily: PP, fontSize: '0.74rem', fontWeight: 600, borderRadius: '8px',
                            bgcolor: filter === f.key ? '#1A8CFF' : '#F1F5F9',
                            color: filter === f.key ? '#fff' : '#475569',
                            '&:hover': { bgcolor: filter === f.key ? '#1A8CFF' : '#E2E8F0' },
                        }}
                    />
                ))}
            </Stack>
            )}

            {/* The month grid reads the SEARCH but not the Installation filter: a calendar
                is already a date range, and two date filters fighting each other is how a
                page ends up empty for no visible reason. */}
            {view === 'calendar' && (
                <SchedulingCalendar
                    rows={searched} month={month} onMonth={setMonth} onOpen={openRow} today={today}
                />
            )}

            {view === 'table' && (
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small" sx={{ '& td, & th': { px: 1.25 } }}>
                        <TableHead>
                            <TableRow>
                                {[
                                    { key: 'customer', label: 'Customer' },
                                    { key: 'agent', label: 'Agent' },
                                    // Deposit first: it is where the job starts, and it reads
                                    // oddly to see an installation date with no deposit beside
                                    // it. (user 2026-10-05: "can add also deposit date before
                                    // site visit date")
                                    { key: 'depositOn', label: 'Deposit' },
                                    // Next to Deposit because that is when it happens: the
                                    // bank has to pay before anything downstream moves.
                                    // Sorting needs no special case — the comparator's
                                    // default branch reads any date key.
                                    { key: 'loanApplicationOn', label: 'Loan' },
                                    ...BOOKABLE.map(b => ({ key: b.key, label: b.short })),
                                    { key: 'payment', label: 'Payment' },
                                    { key: 'remarks', label: 'Remarks' },
                                ].map(h => (
                                    <TableCell key={h.key} sx={headSx} sortDirection={sortBy === h.key ? sortDir : false}>
                                        <TableSortLabel
                                            active={sortBy === h.key}
                                            direction={sortBy === h.key ? sortDir : 'asc'}
                                            onClick={() => sortOn(h.key)}
                                            sx={{
                                                fontFamily: PP, fontWeight: 700, fontSize: '0.74rem',
                                                // Every column here sorts, but MUI keeps the arrow
                                                // invisible until you hover it — so the board looked
                                                // like Installation was the only one, because it was
                                                // the only one carrying an arrow. A faint arrow on the
                                                // rest says "this sorts too" without shouting.
                                                // (user 2026-10-06: "table able to sort aso for these
                                                // columns")
                                                '& .MuiTableSortLabel-icon': {
                                                    opacity: sortBy === h.key ? 1 : 0.28,
                                                },
                                                '&:hover .MuiTableSortLabel-icon': { opacity: 0.7 },
                                            }}
                                        >
                                            {h.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell sx={{ ...headSx, ...stickyHeadSx }} align="right">&nbsp;</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={24} />
                                </TableCell></TableRow>
                            )}
                            {!loading && paged.length === 0 && (
                                <TableRow><TableCell colSpan={10} align="center" sx={{ ...cellSx, py: 5, color: '#94A3B8' }}>
                                    Nothing here. Try another filter.
                                </TableCell></TableRow>
                            )}
                            {paged.map(r => (
                                <TableRow
                                    key={r.salesId} hover onClick={() => openRow(r)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderLeft: hasRemark(r, 'pendingPay') ? '3px solid #DC2626'
                                            : hasRemark(r, 'payDue') ? '3px solid #F59E0B'
                                                : '3px solid transparent',
                                    }}
                                >
                                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                        {r.customerName}
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                            #{r.salesId}{r.city ? ` · ${r.city}` : ''}{r.energy ? ` · ${r.energy} kWp` : ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        {/* Name AND team, because "who is going where this week" is
                                            asked team by team — and two agents can share a first name
                                            while never sharing a team. The team names are short (6-11
                                            characters) and nobody belongs to two, so it fits on the
                                            one line rather than needing another grey row under it.
                                            (user 2026-11-02: "here agent can put team ya. like name - team") */}
                                        {r.agentName}{r.groupName ? ` - ${r.groupName}` : ''}
                                        {r.coAgentName && (
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                + {r.coAgentName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{
                                        ...cellSx, color: r.depositOn ? '#1E293B' : '#CBD5E1', whiteSpace: 'nowrap',
                                    }}>
                                        {fmtShort(r.depositOn)}
                                    </TableCell>
                                    {/* Loan Application. A loan sale with no date yet is the
                                        row somebody has to chase, so it carries a chip rather
                                        than the same grey dash a cash sale shows — otherwise
                                        the column reads as empty on both and flags neither. */}
                                    <TableCell sx={{
                                        ...cellSx, color: r.loanApplicationOn ? '#1E293B' : '#CBD5E1',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {r.loanApplicationOn
                                            ? fmtShort(r.loanApplicationOn)
                                            : isLoanSale(r)
                                                ? <Chip size="small" label="Not applied"
                                                    sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700,
                                                          bgcolor: '#FEF3C7', color: '#B45309' }} />
                                                : fmtShort(null)}
                                    </TableCell>
                                    {BOOKABLE.map(s => (
                                        <TableCell key={s.key} sx={{
                                            ...cellSx,
                                            color: r[s.key] ? '#1E293B' : '#CBD5E1', whiteSpace: 'nowrap',
                                        }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <span>{fmtShort(r[s.key])}</span>
                                                {/* The share rides with the date it sends, not in a far
                                                    column — and only when there is something to send, so
                                                    a row with no message carries no dead button.
                                                    (user 2026-10-05: "the whatsapp button put beside the
                                                    installation if have the whatsapp button ready") */}
                                                {/* Tentative until the customer has confirmed it.
                                                    A date off the engineer's schedule is only half
                                                    an agreement. (user 2026-10-05) */}
                                                {/* The site visit has no date while it is being
                                                    arranged, so the cell says so rather than
                                                    sitting empty. (user 2026-10-06) */}
                                                {s.key === 'siteVisitOn' && !r.siteVisitOn && r.siteVisitArrangingOn && (
                                                    <Chip
                                                        size="small"
                                                        label="Arranging"
                                                        sx={{
                                                            height: 17, fontSize: '0.58rem', fontWeight: 700,
                                                            bgcolor: '#FEF3C7', color: '#B45309',
                                                        }}
                                                    />
                                                )}
                                                {s.key === 'installationOn' && r.installationOn && (
                                                    <Chip
                                                        size="small"
                                                        label={r.installationConfirmedOn ? 'Confirmed' : 'Tentative'}
                                                        sx={{
                                                            height: 17, fontSize: '0.58rem', fontWeight: 700,
                                                            bgcolor: r.installationConfirmedOn ? '#DCFCE7' : '#F1F5F9',
                                                            color: r.installationConfirmedOn ? '#166534' : '#64748B',
                                                        }}
                                                    />
                                                )}
                                                {hasMessage(s.key) && canMessage(s.key, jobFromRow(r)) && (
                                                    <Tooltip title={`WhatsApp the ${MILESTONE_MESSAGES[s.key].label.toLowerCase()} details`}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); share(r, s.key); }}
                                                            sx={{ p: 0.25, color: '#128C7E' }}
                                                        >
                                                            <WhatsAppIcon sx={{ fontSize: 17 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ minWidth: 150 }}>
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#475569', fontWeight: 600 }}>
                                            {r._pct}%
                                            <Box component="span" sx={{ color: '#94A3B8', fontWeight: 400 }}>
                                                {' '}· {money(r.paidTotal)}
                                            </Box>
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate" value={Math.min(100, r._pct)}
                                            sx={{
                                                height: 5, borderRadius: 3, mt: 0.4, bgcolor: '#F1F5F9',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 3,
                                                    bgcolor: r._pct >= 100 ? '#16A34A'
                                                        : r._remarks.length ? '#F59E0B' : '#1A8CFF',
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    {/* Remarks: what to say about the job beyond its dates —
                                        Past once the installation day has gone, and Pending
                                        payment while the money is not all in. */}
                                    <TableCell sx={{ minWidth: 120 }}>
                                        {r._remarks.length === 0 ? (
                                            <Box component="span" sx={{ ...cellSx, color: '#CBD5E1' }}>—</Box>
                                        ) : (
                                            <Stack spacing={0.4} alignItems="flex-start">
                                                {r._remarks.map(m => (
                                                    <Chip key={m.key} label={m.label} size="small" sx={{
                                                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                                                        bgcolor: m.bg, color: m.fg,
                                                    }} />
                                                ))}
                                            </Stack>
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={stickySx}>
                                        <Tooltip title="Open the sale">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/accounting/sales/${r.salesId}`); }}>
                                                <OpenInNewIcon sx={{ fontSize: 17, color: '#64748B' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={shownRows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                    sx={{
                        borderTop: '1px solid #E2E8F0',
                        // The two labels are <p> elements and pick up a page-wide paragraph
                        // margin, which drops them off the line the select sits on.
                        '& .MuiTablePagination-toolbar': { fontFamily: PP, fontSize: '0.78rem', minHeight: 44, alignItems: 'center' },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            margin: 0, fontFamily: PP, fontSize: '0.78rem', lineHeight: 1.6,
                        },
                        '& .MuiTablePagination-select': { fontFamily: PP, fontSize: '0.78rem' },
                        '& .MuiTablePagination-actions button': { padding: '6px' },
                    }}
                />
            </Paper>
            )}

            {/* The row, opened. Dates are changed here and saved in one go — the same
                write the sale page's Project Timeline uses, so the two cannot disagree. */}
            <Drawer anchor="right" open={!!open} onClose={() => setOpen(null)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2.5 } }}>
                {open && (
                    <Box sx={{ fontFamily: PP }}>
                        <Stack direction="row" alignItems="flex-start">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
                                    {open.customerName}
                                </Typography>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#64748B' }}>
                                    #{open.salesId} · {open.agentName}{open.groupName ? ` - ${open.groupName}` : ''}{open.coAgentName ? ` + ${open.coAgentName}` : ''}
                                </Typography>
                                {(open.address || open.city) && (
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mt: 0.5 }}>
                                        {[open.address, open.city, open.postcode, open.state].filter(Boolean).join(', ')}
                                    </Typography>
                                )}
                            </Box>
                            <IconButton size="small" onClick={() => setOpen(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Stack>

                        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '10px', bgcolor: '#F8FAFC' }}>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#475569', fontWeight: 600 }}>
                                Paid {money(open.paidTotal)} of {money(open.effectiveTotal)}
                                {' '}· {open._pct}%
                            </Typography>
                            {/* One row per slip, oldest first. Only once they have loaded —
                                a drawer that briefly shows "no payments" on a sale that has
                                eight of them is worse than showing nothing yet. */}
                            {payDocs && payDocs.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                    {payDocs.map(d => (
                                        <Box key={d.id} sx={{
                                            display: 'flex', alignItems: 'baseline', gap: 1,
                                            py: 0.2, borderTop: '1px dashed #E2E8F0',
                                        }}>
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.72rem', color: '#475569',
                                                flex: 1, minWidth: 0, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {d.label}
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.7rem',
                                                color: d.dated ? '#64748B' : '#B45309', whiteSpace: 'nowrap',
                                            }}>
                                                {fmtShort(d.on)}{d.dated ? '' : ' · uploaded'}
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.72rem', fontWeight: 700,
                                                color: '#0F172A', whiteSpace: 'nowrap',
                                            }}>
                                                {money(d.amount)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {(open._remarks || []).map(m => (
                                <Typography key={m.key} sx={{ fontFamily: PP, fontSize: '0.72rem', color: m.fg, mt: 0.25 }}>
                                    {m.key === 'past' ? 'The installation day has gone.'
                                        : m.key === 'pendingPay'
                                            ? (hasRemark(open, 'past')
                                                ? 'Pending payment — the balance is due now the installation has happened.'
                                                : `Pending payment — ${PRE_INSTALL_PCT}% was due a fortnight before the installation.`)
                                            : `${PRE_INSTALL_PCT}% of the total is due a fortnight before the installation.`}
                                </Typography>
                            ))}
                        </Box>

                        {/* The sale itself, so whoever is scheduling it can see what is
                            turning up without opening the sale page. */}
                        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                            <Typography sx={{
                                fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, color: '#94A3B8',
                                letterSpacing: '0.04em', mb: 0.75,
                            }}>
                                SALE DETAILS
                            </Typography>
                            {!spec ? (
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8' }}>
                                    Loading…
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                                    {[
                                        ['System', spec.kwp ? `${spec.kwp} kWp` : '—'],
                                        ['Panels', spec.panels
                                            ? `${spec.panels}${spec.panelWatt ? ` × ${spec.panelWatt}W` : ''}` : '—'],
                                        ['Battery', spec.batteryQty
                                            ? `${spec.batteryQty}${spec.batteryKwh ? ` · ${spec.batteryKwh} kWh` : ''}`
                                            : 'None'],
                                        ['Phase', spec.phase || '—'],
                                        ['Inverter', spec.inverterKw ? `${spec.inverterKw} kW` : '—'],
                                        ['Scheme', spec.scheme || '—'],
                                        // Off the row, not the spec: whoever is booking a
                                        // crew needs to know a bank is paying, because that
                                        // is the sale whose money can still fall through.
                                        // (user 2026-10-11)
                                        ['Payment', open.paymentMethod ? paymentLabel(open.paymentMethod) : '—'],
                                    ].map(([k, v]) => (
                                        <Box key={k}>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.65rem', color: '#94A3B8' }}>{k}</Typography>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, color: '#1E293B' }}>{v}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={1.5}>
                            {drawerFieldsFor(open).map(f => (
                                <Box key={f.key}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <TextField
                                        type="date" label={f.label} size="small" fullWidth
                                        value={draft[f.key] || ''}
                                        onChange={e => { setDraft(p => ({ ...p, [f.key]: e.target.value })); setSaved(false); }}
                                        disabled={!canEdit}
                                        InputLabelProps={{ shrink: true, sx: { fontFamily: PP, fontSize: '0.82rem' } }}
                                        helperText={!canEdit && f.key === 'siteVisitOn'
                                            ? 'Read-only — the office keys these dates.' : ''}
                                        sx={{ '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' } }}
                                    />
                                    {/* Its own message, beside its own date. Off until that date is
                                        SAVED — the message quotes the saved sale, not the boxes. */}
                                    {hasMessage(f.key) && (
                                        <Tooltip title={shareReason(open, f.key)
                                            || `WhatsApp the ${MILESTONE_MESSAGES[f.key].label.toLowerCase()} details`}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!canMessage(f.key, jobFromRow(open))}
                                                    onClick={() => share(open, f.key)}
                                                    sx={{
                                                        mt: 0.3, border: '1px solid #E2E8F0', borderRadius: '8px',
                                                        color: '#128C7E',
                                                        // Without this the icon keeps its green whatever the button's
                                                        // state, and a share that cannot be pressed looks pressable.
                                                        '&.Mui-disabled': { color: '#CBD5E1' },
                                                    }}
                                                >
                                                    <WhatsAppIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Stack>

                                {/* Still arranging — the site visit is the one milestone with no
                                    date to key while it is happening. (user 2026-10-06) */}
                                {f.key === 'siteVisitOn' && !draft.siteVisitOn
                                    && open.siteVisitArrangingOn !== undefined && (
                                    <Box sx={{
                                        mt: 0.75, display: 'flex', alignItems: 'center', gap: 1,
                                        px: 1.25, py: 0.75, borderRadius: '10px',
                                        bgcolor: open.siteVisitArrangingOn ? '#FFFBEB' : '#F8FAFC',
                                        border: `1px solid ${open.siteVisitArrangingOn ? '#FDE68A' : '#E2E8F0'}`,
                                    }}>
                                        <Switch
                                            size="small"
                                            checked={!!open.siteVisitArrangingOn}
                                            disabled={!canEdit || arrangeBusy}
                                            onChange={(e) => toggleArranging(e.target.checked)}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.78rem', fontWeight: 700,
                                                color: open.siteVisitArrangingOn ? '#B45309' : '#475569',
                                            }}>
                                                {open.siteVisitArrangingOn ? 'Arranging' : 'Not arranged yet'}
                                            </Typography>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                {open.siteVisitArrangingOn
                                                    ? 'Engineer is fixing a slot and will message the customer'
                                                    : 'Switch on while the engineer works out a slot'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* Installation confirmation, directly under the date it is
                                    about — it is part of that date, not a separate thing at
                                    the foot of the list. (user 2026-10-05: "the confirm can
                                    be below the installation? installation confirmation") */}
                                {f.key === 'installationOn' && (
                                    <Box sx={{
                                        mt: 0.75, display: 'flex', alignItems: 'center', gap: 1,
                                        px: 1.25, py: 0.75, borderRadius: '10px',
                                        bgcolor: open.installationConfirmedOn ? '#F0FDF4' : '#F8FAFC',
                                        border: `1px solid ${open.installationConfirmedOn ? '#BBF7D0' : '#E2E8F0'}`,
                                    }}>
                                        <Switch
                                            size="small"
                                            checked={!!open.installationConfirmedOn}
                                            disabled={!canEdit || !open.installationOn || confirmBusy}
                                            onChange={(e) => toggleConfirmed(e.target.checked)}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{
                                                fontFamily: PP, fontSize: '0.78rem', fontWeight: 700,
                                                color: open.installationConfirmedOn ? '#166534' : '#475569',
                                            }}>
                                                {open.installationConfirmedOn
                                                    ? 'Installation confirmed by the customer'
                                                    : 'Installation not confirmed yet — tentative'}
                                            </Typography>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                {!open.installationOn
                                                    ? 'Key an installation date first'
                                                    : open.installationConfirmedOn
                                                        ? `Marked ${fmtLong(open.installationConfirmedOn)} · changing the date clears this`
                                                        : 'The customer has not replied OK yet'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                </Box>
                            ))}

                        </Stack>

                        {saveErr && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#DC2626', mt: 1.5 }}>
                                {saveErr}
                            </Typography>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
                            {canEdit && (
                                <Button variant="contained" size="small" disabled={busy} onClick={saveDrawer}
                                    sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>
                                    {busy ? 'Saving…' : saved ? 'Saved ✓' : 'Save dates'}
                                </Button>
                            )}
                            <Button variant="text" size="small" onClick={() => navigate(`/accounting/sales/${open.salesId}`)}
                                sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, color: '#64748B' }}>
                                Open the sale
                            </Button>
                        </Stack>

                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8', mt: 2 }}>
                            Deposit {fmtLong(open.depositOn)}
                            {open.completedOn ? ` · completed ${fmtLong(open.completedOn)}` : ''}
                        </Typography>
                    </Box>
                )}
            </Drawer>
        </Box>
    );
}
